import Card from "antd/lib/card";
import Flex from "antd/lib/flex";
import Typography from "antd/lib/typography";
import clsx from "clsx";
import React, { useCallback, useMemo, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { createEditor, Transforms, Descendant } from "slate";
import { withHistory } from "slate-history";
import { Editable, RenderElementProps, RenderLeafProps, Slate, withReact } from "slate-react";

import { Bold } from "@sol/uikit/core/icons/Bold";
import { Code } from "@sol/uikit/core/icons/Code";
import { ListNumbers } from "@sol/uikit/core/icons/Editor/ListNumbers";
import { Image } from "@sol/uikit/core/icons/General/Image";
import { H1 } from "@sol/uikit/core/icons/H1";
import { H2 } from "@sol/uikit/core/icons/H2";
import { H3 } from "@sol/uikit/core/icons/H3";
import { Italic } from "@sol/uikit/core/icons/Italic";
import { Link } from "@sol/uikit/core/icons/Link";
import { Quote } from "@sol/uikit/core/icons/Quote";
import { StrikeThrough } from "@sol/uikit/core/icons/StrikeThrough";
import { Underline } from "@sol/uikit/core/icons/Underline";
import { UnorderedList } from "@sol/uikit/core/icons/UnorderedList";

import styles from "./editor/RichText.module.css";
import { RichTextButton } from "./editor/RichTextButton";
import { RichTextElement } from "./editor/RichTextElement";
import { RichTextLeaf } from "./editor/RichTextLeaf";
import {
    deserializeMarkdownToRichTextDocument,
    isRichTextDocumentEmpty,
    validateSlateStructure,
} from "./editor/serializer";
import { isMarkdown } from "./editor/utils";

export type RichTextDocument = Descendant[];

type RichTextProps = {
    value: RichTextDocument;
    name: string;
    onChange: (value: RichTextDocument) => void;
    placeholder: string;
    isError: boolean;
    label?: string;
    autoFocus?: boolean;
};

const renderElement = (props: RenderElementProps) => <RichTextElement {...props} />;

const renderLeaf = (props: RenderLeafProps) => <RichTextLeaf {...props} />;

const RichText = forwardRef<HTMLDivElement, RichTextProps>((props, ref) => {
    const { value, name, placeholder, onChange, isError, label, autoFocus = true } = props;
    const [t] = useTranslation();

    const { Text } = Typography;

    const defaultValue = [
        {
            type: "paragraph" as const,
            children: [{ text: "" }],
        },
    ];

    const initialValue = useMemo(
        () => validateSlateStructure(isRichTextDocumentEmpty(value) ? defaultValue : value),
        [value, defaultValue],
    );

    const handleChange = (value: RichTextDocument) => {
        // Validation de la structure Slate avant de passer la valeur au parent
        const validatedValue = validateSlateStructure(value);
        onChange?.(validatedValue);
    };

    const editor = useMemo(() => {
        const editorInstance = withReact(withHistory(createEditor()));

        // Images with no children
        const { isVoid } = editorInstance;
        editorInstance.isVoid = element => {
            return element.type === "image" ? true : isVoid(element);
        };
        return editorInstance;
    }, []);

    // Automatic copy/paste to markdown conversion
    const handlePaste = useCallback(
        (event: React.ClipboardEvent) => {
            const pastedText = event.clipboardData.getData("text/plain");

            if (pastedText && isMarkdown(pastedText)) {
                event.preventDefault();

                try {
                    const slateNodes = deserializeMarkdownToRichTextDocument(pastedText);
                    Transforms.insertNodes(editor, slateNodes);
                    const lastNode = slateNodes[slateNodes.length - 1];
                    if (lastNode) {
                        Transforms.move(editor, { distance: 1, unit: "offset" });
                    }
                } catch {}
            }
        },
        [editor],
    );

    return (
        <>
            <span id={`richtext-label-${name}`} className="sr-only">
                {label ?? t(`component.AddBriefDescriptionForm.${name}.label`)}
            </span>

            <Slate editor={editor} initialValue={initialValue} onChange={handleChange}>
                <Card
                    ref={ref}
                    title={
                        <Flex gap={4} wrap>
                            <RichTextButton
                                format="heading-one"
                                icon={H1}
                                type="block"
                                title={t("inputs.RichText.headingOne")}
                            />
                            <RichTextButton
                                format="heading-two"
                                icon={H2}
                                type="block"
                                title={t("inputs.RichText.headingTwo")}
                            />
                            <RichTextButton
                                format="heading-three"
                                icon={H3}
                                type="block"
                                title={t("inputs.RichText.headingThree")}
                            />

                            <RichTextButton
                                format="image"
                                icon={Image}
                                type="block"
                                title={t("inputs.RichText.image")}
                            />
                            <RichTextButton format="link" icon={Link} type="mark" title={t("inputs.RichText.link")} />
                            <RichTextButton format="code" icon={Code} type="block" title={t("inputs.RichText.code")} />
                            <RichTextButton
                                format="citation"
                                icon={Quote}
                                type="block"
                                title={t("inputs.RichText.citation")}
                            />

                            <RichTextButton format="bold" icon={Bold} type="mark" title={t("inputs.RichText.bold")} />
                            <RichTextButton
                                format="italic"
                                icon={Italic}
                                type="mark"
                                title={t("inputs.RichText.italic")}
                            />
                            <RichTextButton
                                format="underline"
                                icon={Underline}
                                type="mark"
                                title={t("inputs.RichText.underline")}
                            />
                            <RichTextButton
                                format="strike-through"
                                icon={StrikeThrough}
                                type="mark"
                                title={t("inputs.RichText.strikeThrough")}
                            />

                            <RichTextButton
                                format="numbered-list"
                                icon={ListNumbers}
                                type="block"
                                title={t("inputs.RichText.numberedList")}
                            />
                            <RichTextButton
                                format="bulleted-list"
                                icon={UnorderedList}
                                type="block"
                                title={t("inputs.RichText.bulletedList")}
                            />
                        </Flex>
                    }
                    size="small"
                    className={clsx(styles.card, isError && styles.cardError, "[&_.ant-card-head]:bg-ant-container")}
                    classNames={{
                        body: "!p-0 [&_div]:py-0",
                        header: "sticky top-0 z-10 mx-px pt-1 pb-1 border-b border-grey-lighter",
                    }}
                >
                    <Text>
                        <Editable
                            id={`richtext-editor-${name}`}
                            aria-labelledby={`richtext-label-${name}`}
                            aria-describedby={placeholder ? `richtext-editor-${name}-placeholder` : undefined}
                            className="p-4"
                            renderElement={renderElement}
                            renderLeaf={renderLeaf}
                            placeholder={placeholder}
                            spellCheck
                            autoFocus={autoFocus}
                            onPaste={handlePaste}
                        />
                    </Text>
                </Card>
            </Slate>
        </>
    );
});

RichText.displayName = "RichText";

export default RichText;
